import Picker from '@emoji-mart/react'
import { tabletQuery } from '@globals/themes/themeConstants'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import SentimentSatisfiedOutlined from '@mui/icons-material/SentimentSatisfiedOutlined'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { T, useT } from '@transifex/react'
import { $createTextNode, $getRoot, $getSelection, $isRangeSelection } from 'lexical'
import { useCallback, useEffect, useState } from 'react'
import {
  DESKTOP_HEIGHT,
  EmojiPickerWrapper,
  MOBILE_HEIGHT,
  MOBILE_MAX_HEIGHT,
} from './EmojiPluginUtils'
import { FILTERED_EMOJIS } from './FilteredEmojis.const'

const emojiButtonStyle: SxProps = {
  position: 'absolute',
  right: 40,
  bottom: 8,
  [tabletQuery]: {
    right: 2,
    transform: 'translateY(-90%)',
  },
}

type Emoji = {
  id: string
  name: string
  native: string
  unified: string
  keywords: string[]
  shortcodes: string
  aliases: string[]
}

type EmojiPluginProps = {
  id: string
  isComment?: boolean
}

const EmojiPlugin: React.FC<EmojiPluginProps> = ({ id, isComment = false }) => {
  const t = useT()
  const [showPicker, setShowPicker] = useState<boolean>(false)
  const isTablet = useMediaQuery(tabletQuery)
  const [editor] = useLexicalComposerContext()
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom')

  const handleEmojiSelect = useCallback(
    (emoji: Emoji) => {
      editor.focus()
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          const textNode = $createTextNode(emoji.native)
          selection.insertNodes([textNode])
        } else {
          const root = $getRoot()
          root.append($createTextNode(emoji.native))
        }
      })
      if (!!showPicker) {
        setShowPicker(false)
      }
    },
    [editor],
  )

  useEffect(() => {
    const handleResize = () => {
      if (editor) {
        const editorElement = editor.getRootElement()
        if (editorElement) {
          const editorRect = editorElement.getBoundingClientRect()
          const spaceBelow = window.innerHeight - editorRect.bottom
          const pickerHeight = isTablet ? MOBILE_HEIGHT : DESKTOP_HEIGHT
          setPosition(spaceBelow < pickerHeight ? 'top' : 'bottom')
        }
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [editor, isTablet])

  return (
    !isTablet && (
      <>
        <Tooltip id={`tooltipCommentEmoji-${id}`} placement="top" title={<T _str="Add emoji" />}>
          <IconButton
            id={`btnCommentEmoji-${id}`}
            aria-label={t('Add a comment emoji')}
            aria-labelledby={`tooltipCommentEmoji-${id}`}
            onClick={e => {
              e.stopPropagation()
              setShowPicker(!showPicker)
            }}
            sx={isComment ? emojiButtonStyle : undefined}>
            <SentimentSatisfiedOutlined />
          </IconButton>
        </Tooltip>
        {!!setShowPicker && showPicker && (
          <ClickAwayListener onClickAway={() => setShowPicker(false)}>
            <EmojiPickerWrapper position={position} isMobile={isTablet}>
              <Picker
                set="facebook"
                onEmojiSelect={handleEmojiSelect}
                previewPosition={isTablet ? 'none' : 'bottom'}
                perLine={isTablet ? 6 : 8}
                exceptEmojis={FILTERED_EMOJIS}
                style={{ maxHeight: isTablet ? MOBILE_MAX_HEIGHT : 'auto' }}
              />
            </EmojiPickerWrapper>
          </ClickAwayListener>
        )}
      </>
    )
  )
}

export default EmojiPlugin
